import { Link } from "react-router-dom";
import { ChevronDown, Plus } from "lucide-react";

import { Button } from "../ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import type { WorkSession } from "@/types/workSession.type";
import { cn, getInitials } from "@/lib/utils";
import { formatDuration } from "@/lib/date.utils";
import WorkSessionRow from "./WorkSessionRow";
import { WorkSessionCard } from "./WorkSessionCard";

export type AllSessionsGroupedDay = {
  date: string;
  dateFormatted: string;
  employees: [string, WorkSession[]][];
};

type AllSessionsGroupedListProps = {
  loading: boolean;
  groupedSessions: AllSessionsGroupedDay[] | undefined;
  openDates: Set<string>;
  onToggleDate: (date: string) => void;
  isSuperuser: boolean;
  onAddSession: (employeeId: string, date: Date) => void;
  onEdit: (session: WorkSession) => void;
  onDelete: (session: WorkSession) => void;
};

function totalHoursForSessions(sessions: WorkSession[]) {
  return sessions.reduce((sum, session) => sum + (session.duration || 0), 0);
}

export function AllSessionsGroupedList({
  loading,
  groupedSessions,
  openDates,
  onToggleDate,
  isSuperuser,
  onAddSession,
  onEdit,
  onDelete,
}: AllSessionsGroupedListProps) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <div
            key={i}
            className="overflow-hidden rounded-xl border border-border bg-card"
          >
            <Skeleton className="h-12 w-full rounded-none" />
            <div className="space-y-3 p-4">
              {[1, 2, 3].map((j) => (
                <Skeleton key={j} className="h-16 w-full rounded-md" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (groupedSessions?.length === 0) {
    return (
      <div className="animate-in fade-in-0 rounded-xl border border-border bg-card p-8 text-center text-muted-foreground shadow-md duration-300">
        Aucun pointage trouvé pour cette période
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groupedSessions?.map(({ date, dateFormatted, employees }, index) => {
        const isOpen = openDates.has(date);
        const totalSessions = employees.reduce(
          (acc, [, sessions]) => acc + sessions.length,
          0
        );
        const totalHours = employees.reduce(
          (acc, [, sessions]) => acc + totalHoursForSessions(sessions),
          0
        );
        const totalEmployees = employees.length;
        const dateTitle =
          dateFormatted.charAt(0).toUpperCase() + dateFormatted.slice(1);

        return (
          <div
            key={date}
            className={cn(
              "animate-in fade-in-0 overflow-hidden rounded-xl border border-border bg-card shadow-md duration-300",
              index > 0 && "mt-4"
            )}
          >
            <Collapsible
              open={isOpen}
              onOpenChange={() => onToggleDate(date)}
              className="group w-full"
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between border-b border-border bg-muted/50 px-6 py-3 transition-colors hover:bg-muted/70"
                >
                  <h3 className="font-semibold text-foreground">{dateTitle}</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {totalEmployees} employé{totalEmployees > 1 ? "s" : ""} ·{" "}
                      {totalSessions} session{totalSessions > 1 ? "s" : ""} ·{" "}
                      {formatDuration(totalHours)}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180" />
                  </div>
                </button>
              </CollapsibleTrigger>

              <CollapsibleContent>
                <div className="divide-y divide-border">
                  {employees.map(([employeeName, employeeSessions]) => {
                    const user = employeeSessions[0]?.user;
                    const displayName =
                      user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : employeeName;
                    const empKey =
                      employeeSessions[0]?.employeeId ?? employeeName;
                    return (
                      <Collapsible
                        key={empKey}
                        defaultOpen
                        className="group/emp w-full"
                      >
                        <div className="px-6">
                          <div className="flex w-full items-center justify-between gap-2 py-3">
                            <div className="flex min-w-0 flex-1 items-center gap-3">
                              <CollapsibleTrigger asChild>
                                <button
                                  type="button"
                                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 transition-opacity hover:opacity-80"
                                  aria-label="Afficher ou masquer les sessions de cet employé"
                                >
                                  <span className="text-xs font-semibold text-primary">
                                    {getInitials(
                                      user?.firstName,
                                      user?.lastName,
                                      employeeName
                                    )}
                                  </span>
                                </button>
                              </CollapsibleTrigger>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Link
                                    to={`/users/${employeeSessions[0].employeeId}`}
                                    className="font-medium text-foreground hover:underline"
                                  >
                                    {displayName}
                                  </Link>
                                  {isSuperuser && (
                                    <span className="text-xs text-muted-foreground">
                                      {employeeSessions[0].company?.name ||
                                        "N/A"}
                                    </span>
                                  )}
                                  <span className="text-xs text-muted-foreground">
                                    {formatDuration(
                                      totalHoursForSessions(employeeSessions)
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-primary hover:text-primary"
                                onClick={() =>
                                  onAddSession(
                                    employeeSessions[0].employeeId,
                                    new Date(date)
                                  )
                                }
                              >
                                <Plus className="mr-1 h-4 w-4" />
                                Ajouter
                              </Button>
                              <CollapsibleTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted/80"
                                  aria-label="Afficher ou masquer les sessions"
                                >
                                  <ChevronDown className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/emp:rotate-180" />
                                </button>
                              </CollapsibleTrigger>
                            </div>
                          </div>

                          <CollapsibleContent>
                            <div className="ml-0 pb-3 sm:ml-12">
                              <div className="hidden overflow-x-auto lg:block">
                                <Table className="min-w-full">
                                  <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                      <TableHead className="h-8 min-w-[100px] text-xs">
                                        Arrivée
                                      </TableHead>
                                      <TableHead className="h-8 min-w-[100px] text-xs">
                                        Départ
                                      </TableHead>
                                      <TableHead className="h-8 min-w-[90px] text-xs">
                                        Durée
                                      </TableHead>
                                      <TableHead className="h-8 min-w-[100px] text-xs">
                                        Statut
                                      </TableHead>
                                      <TableHead className="h-8 min-w-[120px] text-right text-xs">
                                        Actions
                                      </TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {employeeSessions.map((session) => (
                                      <WorkSessionRow
                                        key={session.id}
                                        session={session}
                                        onEdit={onEdit}
                                        onDelete={onDelete}
                                      />
                                    ))}
                                  </TableBody>
                                </Table>
                              </div>

                              <div className="space-y-3 lg:hidden sm:p-4">
                                {employeeSessions.map((session) => (
                                  <WorkSessionCard
                                    key={session.id}
                                    session={session}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                  />
                                ))}
                              </div>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        );
      })}
    </div>
  );
}
